import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import './SuperAdminPage.css';
import '../components/Modal.css';
import Portal from '../components/Portal';

const PLANOS = { free: 'Free', familia: 'Família', premium: 'Premium' };
const PLANO_CORES = { free: 'var(--text3)', familia: 'var(--accent)', premium: 'var(--warn)' };

export default function SuperAdminPage() {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalGrupos: 0, totalUsuarios: 0, ativos: 0 });
  const [editGrupo, setEditGrupo] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    const { data: gruposData } = await supabase
      .from('grupos')
      .select('*')
      .order('created_at', { ascending: false });

    const { data: profilesData } = await supabase
      .from('profiles')
      .select('id, name, email, role, grupo_id, created_at');

    const gruposComUsuarios = (gruposData || []).map(g => ({
      ...g,
      usuarios: (profilesData || []).filter(p => p.grupo_id === g.id),
    }));

    setGrupos(gruposComUsuarios);
    setStats({
      totalGrupos: gruposData?.length || 0,
      totalUsuarios: profilesData?.length || 0,
      ativos: gruposData?.filter(g => g.ativo)?.length || 0,
    });
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  function flash(type, text) {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  }

  async function handleToggleAtivo(grupo) {
    await supabase.from('grupos').update({ ativo: !grupo.ativo }).eq('id', grupo.id);
    await loadData();
  }

  async function handleChangePlano(grupoId, plano) {
    const maxMap = { free: 3, familia: 6, premium: 999 };
    await supabase.from('grupos').update({ plano, max_usuarios: maxMap[plano] }).eq('id', grupoId);
    await loadData();
  }

  async function handleDeleteGrupo(grupo) {
    if (!window.confirm(`Excluir o grupo "${grupo.nome}"?\n\nATENÇÃO: Todos os usuários do grupo perderão acesso. Esta ação não pode ser desfeita.`)) return;
    // Desassociar usuários do grupo
    await supabase.from('profiles').update({ grupo_id: null }).eq('grupo_id', grupo.id);
    await supabase.from('grupos').delete().eq('id', grupo.id);
    flash('success', `Grupo "${grupo.nome}" excluído.`);
    await loadData();
  }

  async function handleEditGrupo(form) {
    const maxMap = { free: 3, familia: 6, premium: 999 };
    await supabase.from('grupos').update({
      nome: form.nome,
      plano: form.plano,
      max_usuarios: form.max_usuarios ? parseInt(form.max_usuarios) : maxMap[form.plano],
    }).eq('id', editGrupo.id);
    flash('success', `Grupo "${form.nome}" atualizado!`);
    setEditGrupo(null);
    await loadData();
  }

  return (
    <div className="superadmin-page fade-in">
      <div className="superadmin-header">
        <div>
          <h2 className="superadmin-title">⚡ Super Admin</h2>
          <p className="text-muted text-sm">Visão geral de todos os grupos do sistema</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={loadData}>↻ Atualizar</button>
      </div>

      {msg.text && (
        <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`}>
          {msg.text}
        </div>
      )}

      {/* Stats */}
      <div className="sa-stats">
        <div className="sa-stat-card">
          <div className="sa-stat-value">{stats.totalGrupos}</div>
          <div className="sa-stat-label">Grupos cadastrados</div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-value">{stats.ativos}</div>
          <div className="sa-stat-label">Grupos ativos</div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-value">{stats.totalUsuarios}</div>
          <div className="sa-stat-label">Usuários totais</div>
        </div>
        <div className="sa-stat-card">
          <div className="sa-stat-value">
            {stats.totalGrupos > 0 ? (stats.totalUsuarios / stats.totalGrupos).toFixed(1) : 0}
          </div>
          <div className="sa-stat-label">Média por grupo</div>
        </div>
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'60px 0', gap:12, color:'var(--text2)' }}>
          <div className="spinner" style={{ width:28, height:28 }} />
          <span>Carregando...</span>
        </div>
      ) : (
        <div className="sa-grupos">
          {grupos.map(g => (
            <div key={g.id} className={`sa-grupo-card ${!g.ativo ? 'inativo' : ''}`}>
              <div className="sa-grupo-header">
                <div className="sa-grupo-info">
                  <div className="sa-grupo-nome">{g.nome}</div>
                  <div className="sa-grupo-data text-xs text-muted">
                    Criado em {new Date(g.created_at).toLocaleDateString('pt-BR')}
                    {' · '}
                    <span style={{ color: PLANO_CORES[g.plano], fontWeight:700 }}>
                      {PLANOS[g.plano]}
                    </span>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <select
                    value={g.plano}
                    onChange={e => handleChangePlano(g.id, e.target.value)}
                    className="sa-plano-select"
                    style={{ color: PLANO_CORES[g.plano] }}
                  >
                    <option value="free">Free (3)</option>
                    <option value="familia">Família (6)</option>
                    <option value="premium">Premium (∞)</option>
                  </select>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditGrupo(g)}
                    title="Editar grupo"
                  >✎</button>
                  <button
                    className={`btn btn-sm ${g.ativo ? 'btn-ghost' : 'btn-ghost'}`}
                    onClick={() => handleToggleAtivo(g)}
                    style={{ color: g.ativo ? 'var(--warn)' : 'var(--accent)' }}
                  >
                    {g.ativo ? '⏸ Desativar' : '▶ Ativar'}
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteGrupo(g)}
                    title="Excluir grupo"
                  >✕ Excluir</button>
                </div>
              </div>

              <div className="sa-usuarios-lista">
                <div className="sa-usuarios-header">
                  <span className="text-xs text-muted">
                    👥 {g.usuarios.length} / {g.max_usuarios === 999 ? '∞' : g.max_usuarios} usuários
                  </span>
                  <div className="sa-progress">
                    <div
                      className="sa-progress-bar"
                      style={{
                        width: g.max_usuarios === 999 ? '30%' : `${Math.min(100, (g.usuarios.length / g.max_usuarios) * 100)}%`,
                        background: g.usuarios.length >= g.max_usuarios ? 'var(--danger)' : 'var(--accent)'
                      }}
                    />
                  </div>
                </div>
                {g.usuarios.map(u => (
                  <div key={u.id} className="sa-usuario-item">
                    <span className="sa-usuario-avatar">{u.name?.[0]?.toUpperCase()}</span>
                    <span className="sa-usuario-nome">{u.name}</span>
                    <span className={`role-badge ${u.role}`}>{u.role === 'admin' ? 'Admin' : 'Usuário'}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editGrupo && (
        <GrupoModal
          grupo={editGrupo}
          onSave={handleEditGrupo}
          onClose={() => setEditGrupo(null)}
        />
      )}
    </div>
  );
}

function GrupoModal({ grupo, onSave, onClose }) {
  const [form, setForm] = useState({
    nome: grupo.nome || '',
    plano: grupo.plano || 'free',
    max_usuarios: grupo.max_usuarios === 999 ? '' : String(grupo.max_usuarios),
  });
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nome.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  }

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-box fade-in" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Editar Grupo</h3>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label>Nome do Grupo</label>
              <input type="text" value={form.nome} onChange={e => set('nome', e.target.value)} autoFocus maxLength={60} />
            </div>
            <div className="form-group">
              <label>Plano</label>
              <select value={form.plano} onChange={e => set('plano', e.target.value)}>
                <option value="free">Free</option>
                <option value="familia">Família</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            <div className="form-group">
              <label>Limite de Usuários</label>
              <input
                type="number"
                value={form.max_usuarios}
                onChange={e => set('max_usuarios', e.target.value)}
                placeholder="Deixe vazio para usar o padrão do plano"
                min="1"
              />
              <span className="field-hint">Padrão: Free=3, Família=6, Premium=ilimitado</span>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <><span className="spinner" /> Salvando...</> : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
}
